import "./index.css";

function getPath() {
  return typeof window !== "undefined" ? window.location.pathname : "/";
}

export function App() {
  const path = getPath();

  return (
    <div className="page">
      <header className="nav">
        <a className="logo" href="/">Acme Inc</a>
        <nav className="nav-links">
          <a href="/">Home</a>
          <a href="/#features">Features</a>
          <a href="/pricing">Pricing</a>
          <a href="/faqs">FAQs</a>
          <a href="/terms">Terms</a>
          <a href="/#contact">Contact</a>
        </nav>
      </header>

      <main className="main">
        {path === "/pricing" && <PricingPage />}
        {path === "/faqs" && <FaqsPage />}
        {path === "/terms" && <TermsPage />}
        {path === "/" && <HomePage />}
        {path !== "/" && path !== "/pricing" && path !== "/faqs" && path !== "/terms" && <HomePage />}
      </main>

      <footer className="footer">
        <span>&copy; {new Date().getFullYear()} Acme Inc. All rights reserved.</span>
        <span className="footer-links">
          <a href="/terms">Terms</a>
          <a href="/faqs">FAQs</a>
        </span>
      </footer>
    </div>
  );
}

function HomePage() {
  return (
    <>
      <section className="hero">
        <h1>Build better products, faster.</h1>
        <p>
          Acme Inc helps teams ship world-class software with modern tooling,
          reliable infrastructure, and exceptional support.
        </p>
        <div className="hero-actions">
          <a className="btn btn-primary" href="/#contact">Get started</a>
          <a className="btn btn-outline" href="/pricing">View pricing</a>
        </div>
      </section>

      <section className="features" id="features">
        <h2>Why teams choose Acme</h2>
        <div className="feature-grid">
          {[
            { title: "Lightning fast", body: "Optimized from the ground up for speed. Your users will notice the difference." },
            { title: "Rock-solid reliability", body: "99.99% uptime SLA backed by redundant infrastructure across three regions." },
            { title: "Developer-first", body: "Clean APIs, comprehensive docs, and SDKs for every major language." },
            { title: "Scales with you", body: "From startup to enterprise. No re-platforming, no surprises." },
            { title: "Secure by default", body: "SOC 2 compliant, end-to-end encryption, and granular access controls." },
            { title: "24/7 support", body: "Real humans available around the clock. Chat with us anytime." },
          ].map(item => (
            <div className="feature-card" key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="about" id="about">
        <h2>About Acme Inc</h2>
        <p>
          Founded in 2020, Acme Inc serves over 2,000 companies worldwide. Our
          mission is to give every engineering team the tools they need to build
          great software without the overhead. We're backed by top-tier investors
          and growing fast.
        </p>
      </section>

      <section className="contact" id="contact">
        <h2>Get in touch</h2>
        <p>
          Have questions? Reach out at{" "}
          <a href="mailto:hello@acme-inc.example">hello@acme-inc.example</a> or
          try our chat widget in the bottom-right corner.
        </p>
      </section>
    </>
  );
}

const PRICING_TIERS = [
  {
    name: "Starter",
    price: "$29",
    period: "/month",
    description: "For side projects and small teams.",
    features: ["Up to 3 members", "10 GB storage", "Email support", "Basic analytics"],
    cta: "Start free trial",
    highlighted: false,
  },
  {
    name: "Pro",
    price: "$99",
    period: "/month",
    description: "For growing teams that need more power.",
    features: ["Up to 15 members", "100 GB storage", "Priority support", "Advanced analytics", "API access", "SSO"],
    cta: "Get Pro",
    highlighted: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "For organizations with advanced needs.",
    features: ["Unlimited members", "Unlimited storage", "Dedicated success manager", "Custom SLAs", "On-prem option", "Audit logs"],
    cta: "Contact sales",
    highlighted: false,
  },
];

function PricingPage() {
  return (
    <section className="section-page">
      <div className="pricing-intro">
        <h1>Simple, transparent pricing</h1>
        <p>Choose the plan that fits your team. Upgrade or downgrade anytime.</p>
      </div>
      <div className="pricing-grid">
        {PRICING_TIERS.map(tier => (
          <div
            key={tier.name}
            className={`pricing-card ${tier.highlighted ? "pricing-card-highlight" : ""}`}
          >
            {tier.highlighted && <span className="pricing-badge">Popular</span>}
            <h2>{tier.name}</h2>
            <div className="pricing-price">
              <span className="pricing-amount">{tier.price}</span>
              <span className="pricing-period">{tier.period}</span>
            </div>
            <p className="pricing-desc">{tier.description}</p>
            <ul className="pricing-features">
              {tier.features.map(f => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <a className={`btn ${tier.highlighted ? "btn-primary" : "btn-outline"}`} href="/#contact">
              {tier.cta}
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  {
    q: "How does the free trial work?",
    a: "You get full access to the Pro plan for 14 days. No credit card required. If you don't cancel, we'll start your subscription at the end of the trial.",
  },
  {
    q: "Can I change plans later?",
    a: "Yes. You can upgrade or downgrade at any time. When upgrading, we'll prorate the difference. When downgrading, the new rate applies at the next billing cycle.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit cards (Visa, Mastercard, Amex), PayPal, and for Enterprise plans we can invoice via ACH or wire.",
  },
  {
    q: "Is there a long-term contract?",
    a: "No. All plans are month-to-month unless you choose an annual plan (which includes a discount). Enterprise agreements can be customized.",
  },
  {
    q: "Do you offer refunds?",
    a: "If you're not satisfied within the first 30 days, contact us for a full refund. No questions asked.",
  },
  {
    q: "Where is my data stored?",
    a: "Data is stored in secure, SOC 2 compliant data centers in the US and EU. You can choose your region at signup.",
  },
];

function FaqsPage() {
  return (
    <section className="section-page">
      <div className="page-intro">
        <h1>Frequently asked questions</h1>
        <p>Everything you need to know about Acme Inc. Can't find an answer? Use the chat widget.</p>
      </div>
      <div className="faq-list">
        {FAQ_ITEMS.map((item, i) => (
          <div className="faq-item" key={i}>
            <h3>{item.q}</h3>
            <p>{item.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function TermsPage() {
  return (
    <section className="section-page terms-page">
      <div className="page-intro">
        <h1>Terms and Conditions</h1>
        <p className="terms-updated">Last updated: January 2026</p>
      </div>
      <div className="terms-content">
        <h2>1. Agreement to terms</h2>
        <p>
          By accessing or using the Acme Inc website and services ("Service"), you agree to be bound by these
          Terms and Conditions. If you disagree with any part of these terms, you may not access the Service.
        </p>

        <h2>2. Use of the service</h2>
        <p>
          You must be at least 18 years old and have the legal capacity to enter into contracts to use our
          Service. You are responsible for maintaining the confidentiality of your account and for all
          activities that occur under your account. You agree to use the Service only for lawful purposes
          and in accordance with these Terms.
        </p>

        <h2>3. Acceptable use</h2>
        <p>
          You agree not to use the Service to: (a) violate any applicable law or regulation; (b) infringe
          the intellectual property or other rights of any third party; (c) transmit any malware, viruses, or
          harmful code; (d) attempt to gain unauthorized access to our systems or other users' accounts;
          (e) use the Service for any purpose that is harmful, fraudulent, or abusive. We reserve the
          right to suspend or terminate accounts that violate these terms.
        </p>

        <h2>4. Subscription and payment</h2>
        <p>
          Paid plans are billed in advance on a monthly or annual basis. Fees are non-refundable except as
          required by law or as explicitly stated in our refund policy. We may change our pricing with
          at least 30 days' notice; continued use after the change constitutes acceptance.
        </p>

        <h2>5. Intellectual property</h2>
        <p>
          The Service and its original content, features, and functionality are owned by Acme Inc and are
          protected by international copyright, trademark, and other intellectual property laws. You retain
          ownership of any data you submit to the Service; you grant us a limited license to use that data
          to provide and improve the Service.
        </p>

        <h2>6. Disclaimer of warranties</h2>
        <p>
          The Service is provided "as is" and "as available" without warranties of any kind, either
          express or implied. We do not warrant that the Service will be uninterrupted, error-free, or
          secure. Your use of the Service is at your sole risk.
        </p>

        <h2>7. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, Acme Inc shall not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or any loss of profits or revenues,
          whether incurred directly or indirectly. Our total liability for any claims arising from or
          related to the Service shall not exceed the amount you paid us in the twelve months preceding
          the claim.
        </p>

        <h2>8. Termination</h2>
        <p>
          We may terminate or suspend your access to the Service immediately, without prior notice, for
          any reason, including breach of these Terms. Upon termination, your right to use the Service
          will cease. Provisions that by their nature should survive termination shall survive.
        </p>

        <h2>9. Governing law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State of
          Delaware, United States, without regard to its conflict of law provisions.
        </p>

        <h2>10. Contact</h2>
        <p>
          Questions about these Terms? Contact us at{" "}
          <a href="mailto:legal@acme-inc.example">legal@acme-inc.example</a>.
        </p>
      </div>
    </section>
  );
}

export default App;
